/**
 * Outside services worth knowing about, and what each one actually does.
 *
 * Kept as data rather than markup so entries can be added without touching the page, and so
 * the risk classification travels with the entry rather than living in a stylesheet.
 *
 * `risk` is the important field. Some of these sell engagement, which every platform this app
 * connects to prohibits — Meta, LinkedIn and X all treat purchased followers or likes as
 * grounds for restricting or removing an account. Listing such a service without saying so
 * would read as an endorsement, so the description says what it is and the page shows a
 * warning. Choosing to use one is a business decision; pretending it is risk-free is not.
 */
export type ToolRisk = 'none' | 'caution' | 'terms-violation';

export interface MarketingTool {
  name: string;
  url: string;
  /** What it does, in plain terms. */
  description: string;
  /** Why it matters to this team specifically, or what to watch for. */
  note?: string;
  risk: ToolRisk;
  /**
   * No 'management' category: ShoutVector already schedules, publishes and reports, so
   * listing paid equivalents pointed the team at competitors for a job already done here.
   */
  category: 'engagement' | 'freelance' | 'platform';
  /** Rough cost, where the service publishes one. */
  pricing?: string;
}

export const TOOL_CATEGORIES: Record<
  MarketingTool['category'],
  { title: string; blurb: string }
> = {
  platform: {
    title: 'Platform consoles',
    blurb:
      'Where the connected accounts are actually administered, and where the apps behind ' +
      'this one are configured. These are the pages to open when something needs fixing.',
  },
  freelance: {
    title: 'Freelance marketplaces',
    blurb: 'Hire people for content, design and campaign work.',
  },
  engagement: {
    title: 'Paid engagement',
    blurb:
      'Services that sell followers, likes and views. Read the warning before using any of these.',
  },
};

export const RISK_LABELS: Record<ToolRisk, string> = {
  none: '',
  caution: 'Mixed quality — vet before buying',
  'terms-violation': 'Breaches platform terms',
};

/**
 * What happened when this was actually tried, on 2026-08-24.
 *
 * Recorded because a first-hand account carries weight a generic warning does not — and
 * because `audience_stats` captured the whole thing hourly, so these are measurements rather
 * than recollection. Update `outcome` when the account review resolves.
 */
export interface CaseStudy {
  date: string;
  account: string;
  service: string;
  summary: string;
  /** Follower readings from audience_stats, hourly-ish. */
  timeline: { at: string; followers: number; note?: string }[];
  consequences: string[];
  /**
   * Why this went the way it did — inference, not something the platform stated. Kept in its
   * own field so the page can label it as a theory rather than letting it read as fact
   * alongside the measured timeline.
   */
  likelyCause?: string;
  /**
   * What to say if an account is locked over this. Kept with the case study because that is
   * where someone will look when it happens again.
   */
  appeal: { guidance: string[]; template: string };
  /** Null while the platform review is still open. */
  outcome: string | null;
}

export const TWICSY_CASE_STUDY: CaseStudy = {
  date: '2026-08-24',
  account: '@solbidsolar',
  service: 'Twicsy',
  summary:
    'Roughly 4,000 Instagram followers bought as a test, plus likes on three posts. The ' +
    'account went from 146 to 4,273 followers in under five hours. Instagram locked it the ' +
    'same evening for suspected compromise, requiring a video selfie and an identity review.',
  timeline: [
    { at: '16:22', followers: 146, note: 'before' },
    { at: '17:08', followers: 1446, note: 'first bulk delivery, +1,300' },
    { at: '17:28', followers: 1505 },
    { at: '18:08', followers: 3801, note: 'second bulk delivery, +2,296' },
    { at: '19:11', followers: 4161 },
    { at: '20:27', followers: 4259, note: 'delivery tailing off' },
    { at: '21:08', followers: 4273, note: 'account locked by Instagram this evening' },
  ],
  consequences: [
    'Instagram locked the account for suspected compromise — a 29× follower jump in an ' +
      'afternoon looks identical to a hijacked account being inflated. Recovery required a ' +
      'video selfie and a review Meta quotes at up to 48 hours.',
    'Engagement rate collapsed. A handful of real likes against 4,273 followers reads as ' +
      'roughly 0.1%, which is a ranking signal — it suppresses reach on everything posted ' +
      'afterwards, not just the posts involved.',
    'Analytics is no longer trustworthy for this account. Bought likes arrive through the ' +
      'same Graph API fields as real ones, so the three affected posts show as best-ever ' +
      'performers and cannot be separated out afterwards.',
    'The followers are expected to be purged, returning the count toward its real value — ' +
      'so the money buys a number that does not last, and damage that does.',
  ],
  likelyCause:
    'The account had Meta Advanced Protection enabled, which watches business accounts for ' +
    'takeover signals and locks aggressively, requiring high-assurance verification to return. ' +
    'That would explain both the speed of the lock and why recovery needed a video selfie ' +
    'rather than an SMS code. The same tactic on the same accounts in previous years produced ' +
    'no lock at all — only a gradual purge. Meta has not stated a reason, so this is inference ' +
    'from the timing and the form the recovery took, not a confirmed cause. Worth knowing ' +
    'either way: Advanced Protection is the right setting for a business account, and the ' +
    'lesson is about the spike, not the security feature.',
  appeal: {
    guidance: [
      'An identity check is not an accusation. It asks whether you are the owner, so answer ' +
        'that and nothing else — volunteering an explanation for the follower growth ' +
        'introduces a subject nobody raised.',
      'Never claim a third party did it. A false statement during review turns inauthentic ' +
        'engagement, which platforms handle by deleting the fake accounts, into lying to the ' +
        'platform, which is treated far more seriously. The seller has payment records and ' +
        'the delivery target, so the claim is also checkable.',
      'Only use the in-app flow or the platform\'s own site. Any service offering to speed up ' +
        'a recovery is a scam aimed at people in exactly this position.',
      'If verification succeeds the account is normally restored, often with the purchased ' +
        'followers stripped. Only write an appeal if you are actually rejected and offered ' +
        'one.',
    ],
    template:
      'This is my business account and I am the owner. I have completed identity ' +
      'verification. I understand the recent increase in followers triggered a security ' +
      'flag — that growth was not organic and I am not seeking to keep it. I would like ' +
      'access restored so I can continue managing the page.',
  },
  outcome:
    'Restored on 2026-09-12, nineteen days after the lock, and recovery was a pain rather ' +
    'than a formality — the review Meta quotes at up to 48 hours took the better part of ' +
    'three weeks. The purchased followers were stripped as part of it: the count fell from ' +
    '4,273 to 2,355 on the day of restoration, a loss of 1,918 in one step, and Instagram ' +
    'kept removing 5-50 a day for the next two and a half weeks, to 2,086 by 30 September. ' +
    'Since then the count has held flat at roughly 2,075 to 2,150.\n\n' +
    'What survived is the more useful finding. Follower demographics, read from the Graph ' +
    'API once the permission was granted, show the remaining audience is 536 in India, 217 ' +
    'in Bangladesh and 157 in the United States, topped by Antananarivo, Luanda, Karachi ' +
    'and Dar es Salaam, with 949 of them recording no gender at all. For a Massachusetts ' +
    'solar company that audience is worth nothing, and it is still the majority of the ' +
    'account. The purge removed the obvious bulk; it did not give the account back.\n\n' +
    'So the full cost was nineteen days locked, a permanently distorted engagement history, ' +
    'and a follower list that still mostly cannot buy anything — against a number that ' +
    'lasted eighteen days. Stats now tracks the demographics over time, which makes the ' +
    'US share the honest measure of recovery rather than the follower total.',
};

export const MARKETING_TOOLS: MarketingTool[] = [
  {
    name: 'Meta Business Suite',
    url: 'https://business.facebook.com/',
    category: 'platform',
    risk: 'none',
    description:
      'Administers the Facebook Pages and linked Instagram accounts this app publishes to — ' +
      'roles and permissions, Page settings, and account security. Where you go to grant ' +
      'someone access, or to check what a connection can actually do.',
    note:
      'Instagram Business accounts are administered here rather than in the Instagram app, ' +
      'which is why an Instagram problem is usually a Business Suite problem. Advanced ' +
      'Protection for business accounts is also set here.',
  },
  {
    name: 'Meta for Developers',
    url: 'https://developers.facebook.com/apps/',
    category: 'platform',
    risk: 'none',
    description:
      'The app behind the Facebook and Instagram connections: OAuth credentials, permissions, ' +
      'webhook subscriptions and app review. This app calls Graph API v18.0 and v21.0.',
    note:
      'Reconnecting is how a connection gains a new permission — permissions are granted at ' +
      'authorisation time, so a connection made before a feature shipped will not have what ' +
      'that feature needs. Changing scopes here does nothing until the user reconnects.',
  },
  {
    name: 'Graph API Explorer',
    url: 'https://developers.facebook.com/tools/explorer/',
    category: 'platform',
    risk: 'none',
    description:
      'Runs Graph API calls by hand against a chosen token and permission set. The fastest way ' +
      'to tell a missing permission from a genuine failure.',
    note:
      'Worth knowing: the Graph API omits fields you lack permission for rather than ' +
      'returning an error, so a field simply missing from the response usually means a missing ' +
      'permission, not missing data.',
  },
  {
    name: 'X Developer Portal',
    url: 'https://developer.x.com/en/portal/dashboard',
    category: 'platform',
    risk: 'none',
    description:
      'The app behind the X connection: API keys, OAuth 2.0 settings, scopes and access tier. ' +
      'This app calls the v2 API.',
    note:
      'Media upload needs the `media.write` scope, which is granted at authorisation — so ' +
      'adding it here requires disconnecting and reconnecting X before images will attach.',
  },
  {
    name: 'LinkedIn Developers',
    url: 'https://www.linkedin.com/developers/apps',
    category: 'platform',
    risk: 'none',
    description:
      'The app behind the LinkedIn connection: OAuth credentials, products and permissions. ' +
      'This app calls the v2 API.',
    note:
      'Posting to a company page needs the Community Management API product, which is ' +
      'requested here and reviewed by LinkedIn.',
  },
  {
    name: 'Twicsy',
    url: 'https://twicsy.com/',
    category: 'engagement',
    risk: 'terms-violation',
    pricing: 'From roughly $2 per 100 likes; follower packages scale into the hundreds of dollars',
    description:
      'Sells Instagram, TikTok, X and YouTube followers, likes and views, delivered to a handle ' +
      'you supply. No content or strategy work — it inflates the counter on an account you own.',
    note:
      'Purchased engagement is prohibited by Instagram, Facebook, LinkedIn and X alike, and the ' +
      'accounts at risk are the ones connected here: SolBid and HorizonXL publish through this ' +
      'app on OAuth tokens, so a restriction lands on the pages themselves. This was tried on ' +
      '@solbidsolar on 2026-08-24 — see what happened, below.',
  },
  {
    name: 'Fiverr',
    url: 'https://www.fiverr.com/',
    category: 'freelance',
    risk: 'caution',
    pricing: 'Gigs from $5; realistic social work $50–500 per project',
    description:
      'A freelance marketplace. Relevant categories are social media marketing, content ' +
      'writing, video editing and graphic design — the work behind a post rather than the ' +
      'posting itself.',
    note:
      'Quality varies widely, so treat ratings as a starting point and ask for samples in your ' +
      'own sector. Note that Fiverr also hosts sellers offering the purchased-engagement ' +
      'services described above; those carry the same platform risk wherever they are bought.',
  },
];
